import { useState, useCallback, useEffect } from 'react';
import { message } from 'antd';
import { paymentTermsApi, F116 } from '../../../services/paymentTermsApi';

export type Mode = 'browse' | 'add' | 'edit';

const emptyForm = (): Omit<F116, 'f116_91' | 'f116_92' | 'f116_93'> => ({
  f116_01: '', f116_11: '', f116_12: 0, f116_13: 1, f116_14: false,
  f116_21: 0, f116_22: 0, f116_23: 0, f116_24: 0, f116_31: '',
});

export function usePaymentTermsForm() {
  const [mode, setMode] = useState<Mode>('browse');
  const [records, setRecords] = useState<F116[]>([]);
  const [currentIdx, setCurrentIdx] = useState(-1);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState(emptyForm());

  const current = currentIdx >= 0 && currentIdx < records.length ? records[currentIdx] : null;
  const isEditing = mode !== 'browse';

  // BR-C03: 計算比例模式勾選狀態
  const isRatioMode = form.f116_21 + form.f116_22 + form.f116_23 === 100;

  const loadRecords = useCallback(async () => {
    setLoading(true);
    try {
      const res = await paymentTermsApi.list(1, 1000, true);
      setRecords(res.data || []);
      return res.data || [];
    } catch {
      message.error('載入失敗');
      return [];
    } finally {
      setLoading(false);
    }
  }, []);

  // Sync form from current record
  useEffect(() => {
    if (current && mode === 'browse') {
      setForm({
        f116_01: current.f116_01, f116_11: current.f116_11, f116_12: current.f116_12,
        f116_13: current.f116_13, f116_14: current.f116_14,
        f116_21: current.f116_21, f116_22: current.f116_22,
        f116_23: current.f116_23, f116_24: current.f116_24, f116_31: current.f116_31,
      });
    }
  }, [current, mode]);

  useEffect(() => { loadRecords().then(d => { if (d.length > 0) setCurrentIdx(0); }); }, [loadRecords]);

  const navFirst = () => { if (!isEditing && records.length > 0) setCurrentIdx(0); };
  const navPrev = () => { if (!isEditing && currentIdx > 0) setCurrentIdx(currentIdx - 1); };
  const navNext = () => { if (!isEditing && currentIdx < records.length - 1) setCurrentIdx(currentIdx + 1); };
  const navLast = () => { if (!isEditing && records.length > 0) setCurrentIdx(records.length - 1); };

  const startAdd = () => { setMode('add'); setForm(emptyForm()); };

  const startEdit = () => {
    if (!current) return;
    setMode('edit');
    setForm({
      f116_01: current.f116_01, f116_11: current.f116_11, f116_12: current.f116_12,
      f116_13: current.f116_13, f116_14: current.f116_14,
      f116_21: current.f116_21, f116_22: current.f116_22,
      f116_23: current.f116_23, f116_24: current.f116_24, f116_31: current.f116_31,
    });
  };

  const cancel = () => {
    setMode('browse');
    if (current) {
      setForm({
        f116_01: current.f116_01, f116_11: current.f116_11, f116_12: current.f116_12,
        f116_13: current.f116_13, f116_14: current.f116_14,
        f116_21: current.f116_21, f116_22: current.f116_22,
        f116_23: current.f116_23, f116_24: current.f116_24, f116_31: current.f116_31,
      });
    }
  };

  // BR-C01: 自動組合說明文字
  const buildDescription = useCallback((f21: number, f22: number, f23: number, f24: number) => {
    let desc = `下單預付款比例為${f21}%,出貨前付款比例為${f22}%,出貨後付款比例為${f23}%.`;
    if (f23 > 0) {
      desc += `余額付款天數:${f24}天`;
    }
    return desc;
  }, []);

  // BR-F11: 切換比例模式
  const toggleRatioMode = (checked: boolean) => {
    if (checked) {
      setForm(prev => ({
        ...prev,
        f116_21: 0, f116_22: 0, f116_23: 0, f116_24: 0,
        f116_11: buildDescription(0, 0, 0, 0),
      }));
    } else {
      setForm(prev => ({
        ...prev,
        f116_11: '', f116_21: 0, f116_22: 0, f116_23: 0, f116_24: 0,
      }));
    }
  };

  // Update ratio field and auto-compute description
  const setRatioField = (field: 'f116_21' | 'f116_22' | 'f116_23' | 'f116_24', value: number) => {
    setForm(prev => {
      const next = { ...prev, [field]: value };
      const sum = next.f116_21 + next.f116_22 + next.f116_23;
      if (sum > 0) {
        next.f116_11 = buildDescription(next.f116_21, next.f116_22, next.f116_23, next.f116_24);
      }
      return next;
    });
  };

  const setField = <K extends keyof typeof form>(key: K, value: (typeof form)[K]) => {
    setForm(prev => ({ ...prev, [key]: value }));
  };

  const save = async () => {
    try {
      if (mode === 'add') {
        await paymentTermsApi.create(form);
        message.success('新增成功');
      } else {
        await paymentTermsApi.update(current!.f116_01, form);
        message.success('修改成功');
      }
      const data = await loadRecords();
      const target = mode === 'add' ? form.f116_01 : current!.f116_01;
      const idx = data.findIndex(r => r.f116_01 === target);
      setCurrentIdx(idx >= 0 ? idx : 0);
      setMode('browse');
    } catch (err: any) {
      const vErrs = err?.response?.data?.errors;
      if (vErrs && Array.isArray(vErrs)) {
        vErrs.forEach((e: any) => message.error(e.message));
      } else {
        message.error(err?.response?.data?.error || '儲存失敗');
      }
    }
  };

  const doDelete = async () => {
    if (!current) return;
    try {
      await paymentTermsApi.delete(current.f116_01);
      message.success('刪除成功');
      const data = await loadRecords();
      if (data.length > 0) setCurrentIdx(Math.min(currentIdx, data.length - 1));
      else setCurrentIdx(-1);
    } catch { message.error('刪除失敗'); }
  };

  const doCopy = async (newCode: string) => {
    if (!current) return;
    try {
      await paymentTermsApi.copy(current.f116_01, newCode);
      message.success('複製成功');
      const data = await loadRecords();
      const idx = data.findIndex(r => r.f116_01 === newCode);
      setCurrentIdx(idx >= 0 ? idx : 0);
    } catch (err: any) {
      message.error(err?.response?.data?.error || '複製失敗');
    }
  };

  const quickSearch = (text: string) => {
    if (isEditing) return;
    const idx = records.findIndex(r => r.f116_01 >= text);
    if (idx >= 0) setCurrentIdx(idx);
    else message.warning('查無此代號');
  };

  return {
    mode, isEditing, loading, records, current, currentIdx, form, isRatioMode,
    setField, setRatioField, toggleRatioMode, buildDescription,
    navFirst, navPrev, navNext, navLast,
    startAdd, startEdit, cancel, save, doDelete, doCopy, quickSearch,
  };
}
